/**
 * Enhanced Pilot Job Board
 * Features:
 * - Smart sorting (distance, earnings, rating)
 * - Filtering (distance, pay, customer rating)
 * - Search by location
 * - Performance optimization with React.memo
 * - Estimated earnings calculation
 * - Smart refresh (adaptive refresh rate)
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './PilotJobBoardEnhanced.css';

const PilotJobBoardEnhanced = ({ apiBaseUrl, token, pilotId, user, onJobAccepted, currentStatus, onLogout }) => {
  const [availableJobs, setAvailableJobs] = useState([]);
  const [acceptedJob, setAcceptedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ totalDeliveries: 0, todayDeliveries: 0, earnings: 0, rating: 4.8 });
  
  // Filter & Sort State
  const [sortBy, setSortBy] = useState('earnings'); // 'earnings', 'distance', 'rating'
  const [searchLocation, setSearchLocation] = useState('');
  const [minDistance, setMinDistance] = useState(0);
  const [maxDistance, setMaxDistance] = useState(50);
  const [minPay, setMinPay] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  // Modal State
  const [selectedErrand, setSelectedErrand] = useState(null);
  const [showModal, setShowModal] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchAvailableJobs();
    fetchStats();
    
    // Smart refresh: faster when jobs available, slower when empty
    const getRefreshInterval = () => {
      return availableJobs.length > 0 ? 30000 : 60000; // 30s with jobs, 60s without
    };
    
    const interval = setInterval(() => {
      fetchAvailableJobs();
      fetchStats();
    }, getRefreshInterval());
    
    return () => clearInterval(interval);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchAvailableJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiBaseUrl}/api/v1/pilots/available-jobs?status=submitted`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      
      // Enrich job data with calculated fields
      const enrichedJobs = (data.errands || []).map(job => ({
        ...job,
        estimatedEarnings: job.amount * 0.85, // Estimate after platform fee
        estimatedTime: job.distance_km ? Math.ceil(job.distance_km / 40) : 30, // minutes
      }));
      
      setAvailableJobs(enrichedJobs);
      setLastFetchTime(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) setStats(await response.json());
    } catch (err) {
      console.error('Stats error:', err);
    }
  }, [apiBaseUrl, token]);

  // Memoized filtered and sorted jobs
  const filteredAndSortedJobs = useMemo(() => {
    let jobs = [...availableJobs];

    // Search filter
    if (searchLocation.trim()) {
      const search = searchLocation.toLowerCase();
      jobs = jobs.filter(job =>
        job.pickup_location?.toLowerCase().includes(search) ||
        job.dropoff_location?.toLowerCase().includes(search) ||
        job.title?.toLowerCase().includes(search)
      );
    }

    // Distance filter
    jobs = jobs.filter(job => {
      const distance = job.distance_km || 0;
      return distance >= minDistance && distance <= maxDistance;
    });

    // Pay filter
    jobs = jobs.filter(job => (job.amount || 0) >= minPay);

    // Sorting
    jobs.sort((a, b) => {
      switch (sortBy) {
        case 'earnings':
          return (b.amount || 0) - (a.amount || 0); // Highest pay first
        case 'distance':
          return (a.distance_km || 0) - (b.distance_km || 0); // Closest first
        case 'rating':
          return (b.customer_rating || 0) - (a.customer_rating || 0); // Highest rated first
        default:
          return 0;
      }
    });

    return jobs;
  }, [availableJobs, searchLocation, minDistance, maxDistance, minPay, sortBy]);

  const handleAcceptJob = useCallback(async (errandId) => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/assign-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errand_id: errandId })
      });
      if (response.ok) {
        const data = await response.json();
        setAcceptedJob(data.errand || data);
        onJobAccepted?.(data.errand || data);
        // Refresh jobs after accepting
        fetchAvailableJobs();
      } else {
        setError('Failed to accept job');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, onJobAccepted, fetchAvailableJobs]);

  const resetFilters = useCallback(() => {
    setSearchLocation('');
    setMinDistance(0);
    setMaxDistance(50);
    setMinPay(0);
    setSortBy('earnings');
  }, []);

  const getTimeAgo = (date) => {
    if (!date) return 'Just now';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  return (
    <div className="pilot-job-board-enhanced">
      {/* Header with Stats */}
      <div className="board-header">
        <div className="header-content">
          <h2>📋 Available Errands</h2>
          <p className="refresh-info">Last updated: {getTimeAgo(lastFetchTime)}</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-refresh"
            onClick={fetchAvailableJobs}
            disabled={loading}
            title="Refresh jobs"
          >
            {loading ? '⏳ Loading...' : '🔄 Refresh'}
          </button>
          <button
            className={`btn-filters ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Toggle filters"
          >
            🎚️ Filters
          </button>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="quick-stats">
        <div className="stat-box">
          <span className="stat-label">Available</span>
          <span className="stat-value">{availableJobs.length}</span>
        </div>
        <div className="stat-box">
          <span className="stat-label">Matching</span>
          <span className="stat-value">{filteredAndSortedJobs.length}</span>
        </div>
        <div className="stat-box">
          <span className="stat-label">Today Earnings</span>
          <span className="stat-value">₦{stats.todayDeliveries ? (stats.earnings || 0).toLocaleString() : 0}</span>
        </div>
        <div className="stat-box">
          <span className="stat-label">Rating</span>
          <span className="stat-value">⭐ {stats.rating || 4.8}</span>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filter-panel">
          <div className="filter-group">
            <label>Search Location</label>
            <input
              type="text"
              placeholder="Search pickup or dropoff..."
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
                <option value="earnings">💰 Highest Pay</option>
                <option value="distance">📍 Closest</option>
                <option value="rating">⭐ Best Rated</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Distance: {minDistance}-{maxDistance} km</label>
              <input
                type="range"
                min="0"
                max="100"
                value={maxDistance}
                onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                className="filter-range"
              />
            </div>

            <div className="filter-group">
              <label>Minimum Pay: ₦{minPay.toLocaleString()}</label>
              <input
                type="range"
                min="0"
                max="50000"
                step="1000"
                value={minPay}
                onChange={(e) => setMinPay(parseInt(e.target.value))}
                className="filter-range"
              />
            </div>
          </div>

          <button className="btn-reset-filters" onClick={resetFilters}>
            ↻ Reset Filters
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <span>⚠️ {error}</span>
          <button className="btn-dismiss" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Loading State */}
      {loading && !availableJobs.length && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading available errands...</p>
        </div>
      )}

      {/* Jobs List */}
      {!loading && filteredAndSortedJobs.length > 0 ? (
        <div className="jobs-grid">
          {filteredAndSortedJobs.map((job) => (
            <TinyErrandCard
              key={job.id}
              errand={job}
              onClick={() => {
                setSelectedErrand(job);
                setShowModal(true);
              }}
            />
          ))}
        </div>
      ) : (
        !loading && (
          <div className="empty-state">
            <p className="empty-icon">😴</p>
            <p className="empty-title">
              {availableJobs.length === 0 ? 'No errands available' : 'No matching errands'}
            </p>
            <p className="empty-subtitle">
              {availableJobs.length === 0
                ? 'Check back soon for new errand opportunities!'
                : 'Try adjusting your filters to see more errands.'}
            </p>
            {filteredAndSortedJobs.length === 0 && availableJobs.length > 0 && (
              <button className="btn-reset-filters" onClick={resetFilters}>
                Clear Filters
              </button>
            )}
          </div>
        )
      )}

      {/* Errand Details Modal */}
      {showModal && selectedErrand && (
        <ErrandModal
          errand={selectedErrand}
          onClose={() => setShowModal(false)}
          onAccept={handleAcceptJob}
          isLoading={loading}
          apiBaseUrl={apiBaseUrl}
          token={token}
        />
      )}

      {/* Jobs Count */}
      {!loading && filteredAndSortedJobs.length > 0 && (
        <div className="jobs-footer">
          <p>
            Showing {filteredAndSortedJobs.length} of {availableJobs.length} available {availableJobs.length === 1 ? 'errand' : 'errands'}
          </p>
          <p className="potential-earnings">
            💰 Potential earnings: ₦{filteredAndSortedJobs.reduce((sum, job) => sum + (job.amount || 0), 0).toLocaleString()}
          </p>
        </div>
      )}

      {/* Active Errand Section */}
      {acceptedJob && (
        <div className="active-errand-section">
          <h3>🚗 Current Errand</h3>
          <div className="active-job-card">
            <div className="active-job-header">
              <h4>{acceptedJob.title}</h4>
              <span className="status-badge">In Progress</span>
            </div>
            <p className="active-job-description">{acceptedJob.description}</p>
            <div className="active-locations">
              <div className="location">
                <span className="icon">📍</span>
                <div>
                  <p className="label">Pickup</p>
                  <p className="address">{acceptedJob.pickup_location}</p>
                </div>
              </div>
              <div className="location">
                <span className="icon">🎯</span>
                <div>
                  <p className="label">Dropoff</p>
                  <p className="address">{acceptedJob.dropoff_location}</p>
                </div>
              </div>
            </div>
            <div className="active-errand-actions">
              <button className="btn-start-errand">🚀 Start Errand</button>
              <button className="btn-decline-errand">❌ Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized Job Card Component for Performance
const TinyErrandCard = React.memo(({ errand, onClick }) => {
  const getEarningsBadge = (amount) => {
    if (!amount || amount === 0) return '💼';
    if (amount >= 10000) return '💰💰💰';
    if (amount >= 5000) return '💰💰';
    return '💰';
  };

  const displayAmount = errand.amount && errand.amount > 0 ? `₦${errand.amount.toLocaleString()}` : 'TBD';

  return (
    <div className="tiny-errand-card" onClick={onClick}>
      <div className="tiny-card-header">
        <p className="tiny-card-title">{errand.title || 'Errand'}</p>
        <span className="tiny-earnings-badge">{getEarningsBadge(errand.amount)}</span>
      </div>
      <p className="tiny-card-amount">{displayAmount}</p>
      <div className="tiny-card-indicator">👆 Click for details</div>
    </div>
  );
});

TinyErrandCard.displayName = 'TinyErrandCard';

// Errand Details Modal Component
const ErrandModal = React.memo(({ errand, onClose, onAccept, isLoading, apiBaseUrl, token }) => {
  const [attachments, setAttachments] = React.useState([]);

  // Fetch attachments when modal opens
  React.useEffect(() => {
    const fetchAttachments = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/v1/errands/${errand.id}/attachments`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAttachments(Array.isArray(data) ? data : data.attachments || []);
        }
      } catch (err) {
        console.error('Error fetching attachments:', err);
        setAttachments([]);
      }
    };

    if (errand?.id) {
      fetchAttachments();
    }
  }, [errand?.id, apiBaseUrl, token]);

  const getEarningsBadge = (amount) => {
    if (!amount || amount === 0) return '💼';
    if (amount >= 10000) return '💰💰💰';
    if (amount >= 5000) return '💰💰';
    return '💰';
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAcceptClick = () => {
    onAccept(errand.id);
  };

  return (
    <div className="errand-modal-overlay" onClick={handleBackdropClick}>
      <div className="errand-modal">
        <button className="modal-close-btn" onClick={onClose} title="Close">
          ✕
        </button>

        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-title">{errand.title || 'Errand Details'}</h2>
            <p className="modal-reference">Ref: {errand.reference_number || `#${errand.id}`}</p>
          </div>
          <div className="modal-earnings-badge">{getEarningsBadge(errand.amount)}</div>
        </div>

        <div className="modal-content">
          {/* Description */}
          {errand.description ? (
            <section className="modal-section">
              <h3 className="section-title">📝 Description</h3>
              <p className="modal-description">{errand.description}</p>
            </section>
          ) : (
            <section className="modal-section">
              <h3 className="section-title">📝 Description</h3>
              <p className="modal-description" style={{ color: '#999' }}>No description provided</p>
            </section>
          )}

          {/* Locations */}
          <section className="modal-section">
            <h3 className="section-title">📍 Locations</h3>
            <div className="locations-stack">
              <div className="location-item pickup">
                <span className="location-icon">📦 Pickup</span>
                <p className="location-address">{errand.pickup_location || 'Location TBD'}</p>
              </div>
              <div className="location-arrow">↓</div>
              <div className="location-item dropoff">
                <span className="location-icon">🎯 Dropoff</span>
                <p className="location-address">{errand.dropoff_location || 'Location TBD'}</p>
              </div>
            </div>
          </section>

          {/* Customer Info */}
          <section className="modal-section">
            <h3 className="section-title">👤 Customer</h3>
            <div className="customer-info">
              <div className="customer-header">
                <p className="customer-name">{errand.customer_name || 'Customer'}</p>
                <span className="customer-rating">⭐ {errand.customer_rating ? errand.customer_rating.toFixed(1) : 'New customer'}</span>
              </div>
              {errand.customer_phone && (
                <p className="customer-contact">📞 {errand.customer_phone}</p>
              )}
              {!errand.customer_phone && (
                <p className="customer-contact" style={{ color: '#999' }}>Contact info pending</p>
              )}
            </div>
          </section>

          {/* Attachments */}
          {attachments && attachments.length > 0 && (
            <section className="modal-section">
              <h3 className="section-title">📎 Files ({attachments.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {attachments.map((attachment) => (
                  <div 
                    key={attachment.id}
                    style={{ 
                      padding: 10, 
                      background: '#fef3c7', 
                      border: '1px solid #fcd34d', 
                      borderRadius: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                        📄 {attachment.original_filename || attachment.filename || 'File'}
                      </p>
                      {attachment.size_bytes && (
                        <p style={{ margin: 0, fontSize: 12, color: '#78350f' }}>
                          {(attachment.size_bytes / 1024).toFixed(2)} KB
                        </p>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${apiBaseUrl}/attachments/${attachment.id}`, {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = attachment.original_filename || 'download';
                            document.body.appendChild(link);
                            link.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(link);
                          } else {
                            alert('Failed to download file');
                          }
                        } catch (err) {
                          console.error('Download error:', err);
                          alert('Error downloading file');
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#f59e0b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'pointer',
                        marginLeft: 12,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#d97706'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#f59e0b'}
                    >
                      ⬇️ Download
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Special Instructions / Notes */}
          {errand.note && (
            <section className="modal-section">
              <h3 className="section-title">📋 Special Instructions</h3>
              <p style={{ fontSize: 13, color: '#6b7280', padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b', margin: 0 }}>
                {errand.note}
              </p>
            </section>
          )}

          {/* Errand Details */}
          <section className="modal-section">
            <h3 className="section-title">ℹ️ Errand Details</h3>
            <div className="errand-details-grid">
              <div className="detail-item">
                <span className="detail-label">📏 Distance</span>
                <span className="detail-value">{errand.distance_km ? `${errand.distance_km} km` : 'TBD'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">⏱️ Est. Time</span>
                <span className="detail-value">{errand.estimatedTime || 30} min</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">💵 Base Pay</span>
                <span className="detail-value earning">{errand.amount && errand.amount > 0 ? `₦${errand.amount.toLocaleString()}` : 'Pricing pending'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">💰 Your Earnings*</span>
                <span className="detail-value earning">{errand.estimatedEarnings && errand.estimatedEarnings > 0 ? `₦${errand.estimatedEarnings.toLocaleString()}` : 'Pending'}</span>
              </div>
            </div>
            <p className="modal-note">*After service fee & taxes</p>
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Not Now
          </button>
          <button
            className="btn-accept-modal"
            onClick={handleAcceptClick}
            disabled={isLoading}
          >
            {isLoading ? '⏳ Accepting...' : '✅ Accept Errand'}
          </button>
        </div>
      </div>
    </div>
  );
});

ErrandModal.displayName = 'ErrandModal';

export default PilotJobBoardEnhanced;
